import { CircleFlag } from 'react-circle-flags';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { WithdrawalFxQuote } from '@/services/wallet.service';
import {
  getTargetCurrency,
  normalizeWalletCurrency,
  type WalletCurrency,
  type WithdrawalMode,
} from '../utils/withdrawalConversion';

const MAX_WITHDRAWAL_LIMIT_CENTS: Record<string, number> = {
  USD: 25000000,
  BRL: 25000000,
};

const CURRENCY_FLAGS: Record<WalletCurrency, string> = {
  BRL: 'br',
  USD: 'us',
};

function formatAmountValue(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

interface CurrencySelectProps {
  value: WalletCurrency;
  options: WalletCurrency[];
  onChange?: (currency: WalletCurrency) => void;
  disabled?: boolean;
  'data-cy'?: string;
}

function CurrencySelect({
  value,
  options,
  onChange,
  disabled = false,
  'data-cy': dataCy,
}: CurrencySelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInteractive = !disabled && !!onChange && options.length > 1;

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const content = (
    <>
      <span className="w-5 h-5 overflow-hidden rounded-full shrink-0">
        <CircleFlag countryCode={CURRENCY_FLAGS[value]} height={20} />
      </span>
      <span className="text-sm font-medium text-zinc-200">{value}</span>
      {isInteractive && <ChevronDown className="h-4 w-4 text-zinc-500" aria-hidden />}
    </>
  );

  if (!isInteractive) {
    return (
      <div className="flex items-center gap-2 border-l border-zinc-700 pl-3 shrink-0">
        {content}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative shrink-0 border-l border-zinc-700 pl-3',
        open && 'z-50',
      )}
    >
      <button
        type="button"
        data-cy={dataCy}
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-zinc-800/80 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/50"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {content}
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full z-[200] mt-1 min-w-[120px] overflow-hidden rounded-lg border border-zinc-700 bg-zinc-900 py-1 shadow-lg"
        >
          {options.map((option) => (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={option === value}
              data-cy={`${dataCy}-${option}`}
              onClick={() => {
                onChange?.(option);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-zinc-800',
                option === value ? 'text-primary' : 'text-zinc-200',
              )}
            >
              <span className="w-5 h-5 overflow-hidden rounded-full shrink-0">
                <CircleFlag countryCode={CURRENCY_FLAGS[option]} height={20} />
              </span>
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface WithdrawalAmountSliderProps {
  valueInCents: number;
  onChange: (cents: number) => void;
  walletCurrency: string;
  withdrawalMode: WithdrawalMode;
  convertEnabled?: boolean;
  onSourceCurrencyChange?: (currency: WalletCurrency) => void;
  availableBalanceInCents: number;
  fxQuote?: WithdrawalFxQuote | null;
  isQuoteLoading?: boolean;
  amountLabel?: string;
  receivedLabel?: string;
  className?: string;
}

export default function WithdrawalAmountSlider({
  valueInCents,
  onChange,
  walletCurrency,
  withdrawalMode,
  convertEnabled = false,
  onSourceCurrencyChange,
  availableBalanceInCents,
  fxQuote,
  isQuoteLoading = false,
  amountLabel = 'Qual valor quer sacar?',
  receivedLabel = 'Quanto irá receber após conversão?',
  className,
}: WithdrawalAmountSliderProps) {
  const wallet = normalizeWalletCurrency(walletCurrency);
  const target = getTargetCurrency(wallet);
  const isConverting = withdrawalMode === 'convert';
  const showConversionLayout = convertEnabled && isConverting;

  const platformMaxCents = MAX_WITHDRAWAL_LIMIT_CENTS[wallet] ?? MAX_WITHDRAWAL_LIMIT_CENTS.BRL;
  const effectiveMaxCents = Math.min(
    availableBalanceInCents > 0 ? availableBalanceInCents : 0,
    platformMaxCents,
  );

  const convertedValue =
    isConverting && fxQuote && valueInCents > 0
      ? fxQuote.amount_converted / 100
      : !isConverting && valueInCents > 0
        ? valueInCents / 100
        : null;

  const [formattedValue, setFormattedValue] = useState(() =>
    formatAmountValue(valueInCents / 100),
  );
  const isFocusedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setFormattedValue(formatAmountValue(valueInCents / 100));
    }
  }, [valueInCents]);

  const handleInputChange = (value: string) => {
    const rawValue = value.replace(/\D/g, '');
    const cents = parseInt(rawValue || '0', 10);

    if (availableBalanceInCents <= 0) {
      setFormattedValue(rawValue ? formatAmountValue(cents / 100) : '');
      onChange(cents);
      return;
    }

    if (cents > effectiveMaxCents) {
      setFormattedValue(formatAmountValue(effectiveMaxCents / 100));
      onChange(effectiveMaxCents);
      return;
    }

    setFormattedValue(rawValue ? formatAmountValue(cents / 100) : '');
    onChange(cents);
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
    if (valueInCents === 0) {
      setFormattedValue('');
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    if (valueInCents === 0) {
      setFormattedValue('0,00');
    }
  };

  const inputWrapperClass =
    'relative flex items-center rounded-xl border border-zinc-700/80 bg-zinc-900/60 px-4 py-3 focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/30 transition-colors';

  const sourceCurrencyOptions: WalletCurrency[] = ['BRL', 'USD'];

  const amountInput = (
    <input
      ref={inputRef}
      id="withdrawal-amount-input"
      type="text"
      inputMode="numeric"
      data-cy="input-valor-saque"
      value={formattedValue}
      onChange={(e) => handleInputChange(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className="flex-1 min-w-0 bg-transparent text-lg font-medium text-white outline-none placeholder:text-zinc-600"
      autoComplete="off"
    />
  );

  if (!showConversionLayout) {
    return (
      <div className={cn('space-y-2', className)}>
        <label htmlFor="withdrawal-amount-input" className="text-sm text-zinc-200">
          {amountLabel}
        </label>
        <div className={inputWrapperClass}>
          {amountInput}
          <CurrencySelect
            value={wallet}
            options={sourceCurrencyOptions}
            onChange={onSourceCurrencyChange}
            data-cy="select-moeda-origem"
          />
        </div>
      </div>
    );
  }

  const targetDisplay =
    isQuoteLoading && isConverting ? (
      <div className="flex items-center gap-2 text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">...</span>
      </div>
    ) : (
      <span className="text-lg font-medium text-white">
        {convertedValue != null ? formatAmountValue(convertedValue) : '0,00'}
      </span>
    );

  return (
    <div className={cn('space-y-4', className)}>
      <div className="relative z-20 space-y-2">
        <label htmlFor="withdrawal-amount-input" className="text-sm text-zinc-200">
          {amountLabel}
        </label>
        <div className={inputWrapperClass}>
          {amountInput}
          <CurrencySelect
            value={wallet}
            options={sourceCurrencyOptions}
            onChange={onSourceCurrencyChange}
            data-cy="select-moeda-origem"
          />
        </div>
      </div>

      <div className="relative z-10 space-y-2">
        <p className="text-sm text-zinc-200">{receivedLabel}</p>
        <div className={cn(inputWrapperClass, 'cursor-default')}>
          <div className="flex-1 min-w-0">{targetDisplay}</div>
          <CurrencySelect
            value={target}
            options={[target]}
            data-cy="select-moeda-destino"
          />
        </div>
      </div>
    </div>
  );
}

export { MAX_WITHDRAWAL_LIMIT_CENTS };
